import type { ConfigStoryblok } from '~storyblok-types/ConfigStoryblok';
import type { StoryblokMultilink } from '~storyblok-types/storyblok';
import type { CountryLanguages } from '~/types/countryLanguages';
import { resolveStoryblokLink } from './resolveStoryblokLink';
import log from '~/utils/log';

type PostLoginDefaultStory = ConfigStoryblok['postLoginDefaultStory'];

const resolveConfiguredStoryUrl = (
    story: PostLoginDefaultStory,
    countryLanguages: CountryLanguages,
    lang: string
): string | null => {
    if (!story || typeof story === 'string') {
        return null;
    }

    const storyLink = {
        fieldtype: 'multilink',
        id: story.uuid,
        url: '',
        cached_url: '',
        linktype: 'story',
        story
    } as unknown as StoryblokMultilink;

    try {
        return resolveStoryblokLink(storyLink, countryLanguages, lang);
    } catch (error) {
        log.error('[utils:resolvePostLoginRedirectTarget] Error resolving storyblok link', storyLink, error);
        return null;
    }
};

/**
 * Resolves the post login redirect target based on the configured story.
 * If not found use the fallback.
 * @param story - The story to resolve the post login redirect target for.
 * @param countryLanguages - The country languages to resolve the post login redirect target for.
 * @param lang - The language to resolve the post login redirect target for.
 * @param fallback - The fallback to use if the configured story is not found.
 * @returns The post login redirect target.
 */
export const resolvePostLoginRedirectTarget = (
    story: PostLoginDefaultStory,
    countryLanguages: CountryLanguages,
    lang: string,
    fallback: string
) => {
    const configuredUrl = resolveConfiguredStoryUrl(story, countryLanguages, lang);
    if (!configuredUrl) {
        return fallback;
    }

    return configuredUrl;
};
